import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { LEADING_TRACK_WIDTH } from '@dealstack/frontend/shared/components/Table/MantineTable/utils/pinnedColumn';

interface GenerateGridTemplateColumn<T> {
	id: string;
	columnDef: ExtendedColumnDef<T, unknown>;
}

interface GenerateGridTemplateOptions<T> {
	columns: GenerateGridTemplateColumn<T>[];
	enableRowDragAndDrop: boolean;
	enableRowSelection: boolean;
	showRowSelection: boolean;
	enableRowActions: boolean;
	renderRowActions?: unknown;
	renderRowActionMenuItems?: unknown;
	minWidth: number;
	columnSizing?: Record<string, number>;
}

export function isFixedWidthColumn<T>(columnDef: ExtendedColumnDef<T, unknown>): boolean {
	const minSize = columnDef.minSize;
	const maxSize = columnDef.maxSize;
	return columnDef.meta?.isGrow === false || (!!maxSize && maxSize === minSize);
}

function getColumnGridSize<T>(
	columnDef: ExtendedColumnDef<T, unknown>,
	defaultMinWidth: number
): string {
	const meta = columnDef.meta;
	const size = columnDef.size ?? defaultMinWidth;
	const minSize = columnDef.minSize ?? defaultMinWidth;
	const maxSize = columnDef.maxSize;

	// Number.MAX_SAFE_INTEGER is TanStack Table's default maxSize
	const isDefaultMaxSize = maxSize === Number.MAX_SAFE_INTEGER;

	// Absolute fixed width
	if (isFixedWidthColumn(columnDef)) {
		return `${size}px`;
	}

	// Percentage-based
	if (meta?.widthPercentage) {
		return `${meta.widthPercentage}%`;
	}

	// Bounded flex with reasonable max (but not default MAX_SAFE_INTEGER)
	if (maxSize && !isDefaultMaxSize) {
		return `minmax(${minSize}px, ${maxSize}px)`;
	}

	// Proportional flexible (default or when maxSize is MAX_SAFE_INTEGER)
	const frWeight = size / defaultMinWidth;
	return `minmax(${minSize}px, ${frWeight}fr)`;
}

export function generateGridTemplateColumns<T>(options: GenerateGridTemplateOptions<T>): string {
	const parts: string[] = [];

	if (options.enableRowDragAndDrop) {
		parts.push(`${LEADING_TRACK_WIDTH}px`);
	}

	if (options.enableRowSelection && options.showRowSelection) {
		parts.push(`${LEADING_TRACK_WIDTH}px`);
	}

	const columnTracks = options.columns.map(({ id, columnDef }) => {
		const userSize = options.columnSizing?.[id];
		if (userSize) {
			const minSize = columnDef.minSize ?? options.minWidth;
			return {
				columnDef,
				minPx: Math.max(userSize, minSize),
				track: `${Math.max(userSize, minSize)}px`,
			};
		}
		return {
			columnDef,
			minPx: columnDef.minSize ?? options.minWidth,
			track: getColumnGridSize(columnDef, options.minWidth),
		};
	});

	const hasFlexTrack = columnTracks.some((c) => c.track.includes('fr'));

	if (!hasFlexTrack) {
		for (let i = columnTracks.length - 1; i >= 0; i--) {
			if (!isFixedWidthColumn(columnTracks[i].columnDef)) {
				columnTracks[i].track = `minmax(${columnTracks[i].minPx}px, 1fr)`;
				break;
			}
		}
	}

	columnTracks.forEach((c) => parts.push(c.track));

	if (
		options.enableRowActions &&
		(options.renderRowActions || options.renderRowActionMenuItems)
	) {
		parts.push('80px');
	}

	return parts.join(' ');
}
